import * as zod from 'zod';

import { CartesianPose } from '@sb/geometry';
import { TCPOffsetOption } from '@sb/motion-planning/TCPOffsetOption';
import {
  ActionRequiredError,
  MotionSpeed,
  Step,
} from '@sb/remote-control/types';
import { calculateSpeedProfileFromStepMotionSpeed } from '@sb/remote-control/util/calculateSpeedProfileFromStepMotionSpeed';

export namespace SamplePositioningApproachStep {
  export const argumentKind = 'SamplePositioningApproach';
  export const name = 'Sample positioning approach';
  export const description =
    'Sweeps the robot through waypoints sampled around a target pose, for positioning-skill data collection. Use as a substep of Create recording.';
  export const librarySection = Step.LibrarySection.Basic;
  export const featureFlag = 'samplePositioningApproachStep' as const;

  // 10° default in radians
  const TEN_DEG_RAD = (10 * Math.PI) / 180;

  const OrientationJitter = zod.object({
    rollRange: zod.number().nonnegative().default(TEN_DEG_RAD),
    pitchRange: zod.number().nonnegative().default(TEN_DEG_RAD),
    yawRange: zod.number().nonnegative().default(TEN_DEG_RAD),
  });

  const AxisLocks = zod.object({
    roll: zod.boolean().default(false),
    pitch: zod.boolean().default(false),
    yaw: zod.boolean().default(false),
  });

  // One cylinder of the per-level stack: an absolute radius (m) and a relative
  // height (unitless weight). Heights are normalised across the stack to fill
  // the target -> starting distance, so only their ratios matter.
  const PerLevel = zod.object({
    radius: zod.number().nonnegative(),
    height: zod.number().positive(),
  });

  export type PerLevel = zod.infer<typeof PerLevel>;

  // Default per-level stack mirrors the linear defaults: 8 levels with radius
  // ramping from the narrow (30 mm) to the wide (120 mm) default and equal
  // relative heights — a stepped approximation of the default cone, so the
  // volume is comparable when an operator first switches modes. Radii are
  // rounded to whole millimetres so they display cleanly in the editor.
  const DEFAULT_LEVELS: PerLevel[] = Array.from({ length: 8 }, (_, i) => ({
    radius: Math.round((0.03 + (i / 7) * (0.12 - 0.03)) * 1000) / 1000,
    height: 1,
  }));

  const PoseSourceKind = zod.enum(['singlePosition', 'positionList']);

  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),

    targetPose: CartesianPose.nullable().default(null),
    targetSourceKind: PoseSourceKind.default('singlePosition'),
    targetPositionListID: zod.string().nullable().default(null),

    startingPose: CartesianPose.nullable().default(null),
    startingSourceKind: PoseSourceKind.default('singlePosition'),
    startingPositionListID: zod.string().nullable().default(null),

    bufferLength: zod.number().nonnegative().default(0.01),

    // Linear (min/max) mode: radius interpolates narrow -> wide along the axis.
    narrowRadius: zod.number().nonnegative().default(0.03),
    wideRadius: zod.number().nonnegative().default(0.12),

    // Per-level mode: an explicit stack of {radius, relative height} cylinders.
    // narrow/wide above are ignored when radiusMode is 'perLevel'.
    radiusMode: zod.enum(['linear', 'perLevel']).default('linear'),
    levels: zod.array(PerLevel).default(DEFAULT_LEVELS),

    // Orthogonal to radiusMode. 'continuous' draws each point's height from a
    // Gaussian centred on the target; levelCount becomes the execution-batch
    // count.
    heightMode: zod.enum(['discrete', 'continuous']).default('discrete'),

    // Continuous mode only: Gaussian height spread as a fraction of the
    // approach length (sigma = heightSpread * approachLength).
    heightSpread: zod.number().positive().default(0.5),

    sampleCount: zod.number().int().min(1).default(60),
    levelCount: zod.number().int().min(2).default(8),
    targetEmphasis: zod.number().min(0).max(1).default(0.2),
    randomSeed: zod.number().int().nullable().default(null),

    orientationJitter: OrientationJitter.default({
      rollRange: TEN_DEG_RAD,
      pitchRange: TEN_DEG_RAD,
      yawRange: TEN_DEG_RAD,
    }),
    lockedAxes: AxisLocks.default({ roll: false, pitch: false, yaw: false }),

    // Default wrist flange. No 'auto' — operators choose explicitly which
    // tooltip the target/starting poses are interpreted at.
    tcpOption: TCPOffsetOption.default('wrist'),

    // Motion type for the sampling sweep between waypoints. 'joint' (default)
    // plans in joint space — robust, but can take long paths between
    // Cartesian-close, orientation-jittered poses. 'line' moves the tooltip in
    // straight Cartesian segments — tighter for small radii, but can fail to
    // plan near singularities. The initial move to the target is always joint.
    motionKind: zod.enum(['joint', 'line']).default('joint'),

    // Corner-rounding radius for the sweep, in metres. Capped at 10 mm; 0
    // disables blending so the robot reaches each waypoint exactly. A value
    // larger than the smallest configured radius can blend nearby sample
    // points together (the UI warns when that happens).
    blendRadius: zod.number().min(0).max(0.01).default(0.01),

    motionSpeed: MotionSpeed.optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  // Build the runner-side pose source. For 'positionList' we forward the
  // space ID so the runner re-reads the live pose at play time — this is
  // what makes edits to the referenced Space take effect on the next run.
  // For 'singlePosition' we forward the inline pose.
  const resolvePoseSource = (
    sourceKind: 'singlePosition' | 'positionList',
    pose: CartesianPose | null,
    positionListID: string | null,
    fieldId: 'targetPose' | 'startingPose',
  ) => {
    if (sourceKind === 'positionList') {
      if (!positionListID) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: `${fieldId === 'targetPose' ? 'Target' : 'Starting'} position list must be set`,
          fieldId,
        });
      }

      return { positionListID };
    }

    if (!pose) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: `${fieldId === 'targetPose' ? 'Target' : 'Starting'} pose is not configured`,
        fieldId,
      });
    }

    return pose;
  };

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
    baseSpeedProfile,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    const targetPose = resolvePoseSource(
      args.targetSourceKind,
      args.targetPose,
      args.targetPositionListID,
      'targetPose',
    );

    const startingPose = resolvePoseSource(
      args.startingSourceKind,
      args.startingPose,
      args.startingPositionListID,
      'startingPose',
    );

    return {
      ...stepData,
      stepKind: 'SamplePositioningApproach',
      args: {
        targetPose,
        startingPose,
        bufferLength: args.bufferLength,
        narrowRadius: args.narrowRadius,
        wideRadius: args.wideRadius,
        radiusMode: args.radiusMode,
        levels: args.levels,
        heightMode: args.heightMode,
        heightSpread: args.heightSpread,
        sampleCount: args.sampleCount,
        levelCount: args.levelCount,
        targetEmphasis: args.targetEmphasis,
        randomSeed: args.randomSeed,
        orientationJitter: args.orientationJitter,
        lockedAxes: args.lockedAxes,
        tcpOption: args.tcpOption,
        motionKind: args.motionKind,
        // Operator-controlled (advanced tab), capped at 10 mm; 0 = no blending.
        blendRadius: args.blendRadius,
        speedProfile: calculateSpeedProfileFromStepMotionSpeed({
          baseSpeedProfile,
          motionSpeed: args.motionSpeed,
        }),
      },
    };
  };
}

SamplePositioningApproachStep satisfies Step.StepKindInfo;
